import { useState } from "react";
import { Mail, Send } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CONTACT_EMAIL = "info.okkioapp@gmail.com";
const MOTIVI = [
  "Domanda sul servizio",
  "Domanda sul mio report",
  "Problema con il pagamento",
  "Privacy e dati",
  "Altro",
];

export default function Contatti() {
  const [form, setForm] = useState({ nome: "", email: "", motivo: "", messaggio: "" });
  const [consenso, setConsenso] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.motivo || !form.messaggio.trim()) {
      toast.error("Compila tutti i campi prima di inviare.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    if (!consenso) {
      toast.error("Devi confermare la presa visione dell’Informativa Privacy per inviare il messaggio.");
      return;
    }
    const subject = `[OKKIO] ${form.motivo} — ${form.nome.trim()}`;
    const body = `Nome: ${form.nome.trim()}\nEmail: ${form.email.trim()}\nMotivo: ${form.motivo}\n\nMessaggio:\n${form.messaggio.trim()}`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Ti stiamo aprendo l’email precompilata. Controlla il tuo programma di posta.");
  };

  return (
    <SiteLayout
      seo={{ title: "Contatti", description: "Contatta OKKIO per domande sul servizio di analisi indipendente dei preventivi di ristrutturazione.", path: "/contatti" }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Contatti", to: "/contatti" }]}
      narrow>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">Contatti</h1>
      <p className="mt-5 text-lg text-slate-600 leading-relaxed">
        Hai una domanda sul servizio o sul tuo report? Scrivici, ti rispondiamo il prima possibile.
      </p>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center"><Mail className="w-5 h-5 text-slate-700" strokeWidth={1.75} /></span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</p>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="font-heading font-semibold text-slate-900 hover:underline">{CONTACT_EMAIL}</a>
        </div>
      </div>

      {/* Modulo di contatto */}
      <form onSubmit={submit} data-testid="contact-form" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 space-y-5">
        <h2 className="font-heading text-xl font-semibold text-slate-900">Scrivici un messaggio</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="nome" className="block text-sm font-semibold text-slate-900 mb-1.5">Nome</Label>
            <Input id="nome" data-testid="contact-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)}
              placeholder="Il tuo nome" className="rounded-xl h-12" />
          </div>
          <div>
            <Label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-1.5">Email</Label>
            <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="tu@email.it" className="rounded-xl h-12" />
          </div>
        </div>

        <div>
          <Label className="block text-sm font-semibold text-slate-900 mb-1.5">Motivo</Label>
          <Select value={form.motivo} onValueChange={(v) => set("motivo", v)}>
            <SelectTrigger data-testid="contact-motivo" className="rounded-xl h-12"><SelectValue placeholder="Seleziona un motivo" /></SelectTrigger>
            <SelectContent>{MOTIVI.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="messaggio" className="block text-sm font-semibold text-slate-900 mb-1.5">Messaggio</Label>
          <Textarea id="messaggio" data-testid="contact-messaggio" value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)}
            placeholder="Scrivi qui la tua richiesta" rows={5} className="rounded-xl resize-none" />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={consenso} onCheckedChange={(v) => setConsenso(v === true)} data-testid="contact-consenso" className="mt-0.5" />
          <span className="text-sm text-slate-600 leading-relaxed">
            Dichiaro di aver preso visione dell’<a href="/privacy-policy" className="font-semibold text-[#3157F6] hover:underline">Informativa Privacy</a>. I dati saranno usati solo per rispondere alla mia richiesta.
          </span>
        </label>

        <Button type="submit" data-testid="contact-submit"
          className="w-full sm:w-auto rounded-xl h-12 px-6 text-base font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white active:scale-[0.98] transition-transform">
          Invia messaggio <Send className="w-4 h-4 ml-1" strokeWidth={2} />
        </Button>
      </form>

      <p className="mt-6 text-sm text-slate-500 leading-relaxed">
        OKKIO è una piattaforma indipendente di analisi dei preventivi. Non forniamo preventivi né serviamo imprese edili.
      </p>
    </SiteLayout>
  );
}
